import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { Link } from "react-router-dom";
import { Landmark, AlertTriangle, CheckCircle2, Clock, CalendarClock, ArrowRight } from "lucide-react";

const CFG = {
  CURRENT: { cls: "border-emerald-500 bg-emerald-50 text-emerald-800", Icon: CheckCircle2, label: "All caught up" },
  NO_SCHEDULE: { cls: "border-neutral-300 bg-neutral-50 text-muted-foreground", Icon: Landmark, label: "No schedule yet" },
  UPCOMING: { cls: "border-sky-400 bg-sky-50 text-sky-800", Icon: CalendarClock, label: "Upcoming" },
  DUE_SOON: { cls: "border-amber-400 bg-amber-50 text-amber-900", Icon: Clock, label: "Due soon" },
  DUE_TODAY: { cls: "border-orange-500 bg-orange-50 text-orange-900", Icon: Clock, label: "Due today" },
  OVERDUE: { cls: "border-red-500 bg-red-50 text-red-800", Icon: AlertTriangle, label: "Overdue" },
  ADJUSTMENT_REQUIRED: { cls: "border-purple-500 bg-purple-50 text-purple-800", Icon: AlertTriangle, label: "Adjustment required" },
};

const dayCount = (d) => {
  if (d == null) return "";
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} overdue`;
  if (d === 0) return "due today";
  return `due in ${d} day${d === 1 ? "" : "s"}`;
};

// Human-readable headline per status (source of truth = backend selector).
const headline = (o) => {
  switch (o.status) {
    case "CURRENT": return "No CRA remittance currently due";
    case "NO_SCHEDULE": return "No posted payroll yet — CRA remittance schedule appears after your first posted run";
    case "ADJUSTMENT_REQUIRED": return "CRA remittance adjustment required";
    case "OVERDUE": return o.overdue_count > 1 ? `${o.overdue_count} CRA remittances overdue` : "CRA remittance overdue";
    case "DUE_TODAY": return "CRA remittance due today";
    case "DUE_SOON": return "CRA remittance due soon";
    default: return "Upcoming CRA remittance";
  }
};

export default function NextRemittance({ companyId, remittanceHref, variant = "banner", onFocus }) {
  const [o, setO] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/remittance/next-obligation`)
      .then((r) => setO(r.data)).catch(() => setO(null));
  }, [companyId]);

  if (!o) return null;
  const cfg = CFG[o.status] || CFG.UPCOMING;
  const Icon = cfg.Icon;
  const showAmount = o.has_obligation;
  const extraOverdue = o.has_obligation && o.status !== "OVERDUE" && o.overdue_count > 0;

  // Deep-link that resolves the EXACT company + period + year (never row position/text).
  const deepHref = (() => {
    if (!remittanceHref) return null;
    if (!o.has_obligation || !o.period_key) return remittanceHref;
    const sep = remittanceHref.includes("?") ? "&" : "?";
    return `${remittanceHref}${sep}period=${encodeURIComponent(o.period_key)}&pyear=${o.year}`;
  })();

  if (variant === "card") {
    const clickable = o.has_obligation && typeof onFocus === "function";
    return (
      <div data-testid="next-remit-card" onClick={clickable ? () => onFocus(o.period_key, o.year) : undefined}
        className={`border ${cfg.cls} mb-6 ${clickable ? "cursor-pointer hover:opacity-95 transition-opacity" : ""}`}>
        <div className="px-5 py-4 flex flex-wrap items-center gap-4">
          <Icon className="h-6 w-6 shrink-0" />
          <div className="flex-1 min-w-[180px]">
            <div className="flex items-center gap-2">
              <span data-testid="next-remit-status" className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-current/40 bg-white/50">{cfg.label}</span>
              {o.remitter_type && <span className="text-[10px] font-mono uppercase tracking-widest text-current/70">{o.remitter_type}</span>}
              {o.rp_account && <span className="text-[10px] font-mono text-current/70">{o.rp_account}</span>}
            </div>
            <div className="mt-1 font-bold tracking-tight">{headline(o)}</div>
            {o.has_obligation && (
              <div className="mt-0.5 text-sm text-current/80">
                {o.period_label} · due {o.due_date} · <span className="font-medium">{dayCount(o.days)}</span>
                {o.partially_paid && <span className="ml-1">· partially paid ({money(o.amount_paid)})</span>}
              </div>
            )}
            {extraOverdue && (
              <div data-testid="next-remit-overdue-count" className="mt-0.5 text-xs font-medium text-red-700">
                ⚠ {o.overdue_count} other remittance{o.overdue_count === 1 ? "" : "s"} overdue
              </div>
            )}
          </div>
          {showAmount && (
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-current/70">Balance due</div>
              <div data-testid="next-remit-amount" className="text-2xl font-black tabular-nums">{money(o.balance_due)}</div>
              {clickable && <div className="mt-0.5 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 text-current/70">View period <ArrowRight className="h-3 w-3" /></div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Compact Overview banner.
  return (
    <div data-testid="next-remittance-banner" className={`border ${cfg.cls} mb-6`}>
      <div className="px-4 py-3 flex flex-wrap items-center gap-3">
        <Icon className="h-5 w-5 shrink-0" />
        <span data-testid="next-remit-status" className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-current/40 bg-white/50">{cfg.label}</span>
        <div className="flex-1 min-w-[160px]">
          <span className="font-bold tracking-tight">{headline(o)}</span>
          {o.has_obligation && (
            <span data-testid="next-remit-due" className="ml-2 text-sm text-current/80">
              {o.period_label} · due {o.due_date} · {dayCount(o.days)}
            </span>
          )}
          {extraOverdue && (
            <span data-testid="next-remit-overdue-count" className="ml-2 text-xs font-medium text-red-700">(+{o.overdue_count} overdue)</span>
          )}
        </div>
        {o.has_obligation && (
          <span data-testid="next-remit-amount" className="text-lg font-black tabular-nums">{money(o.balance_due)}</span>
        )}
        {deepHref && (
          <Link data-testid="next-remit-link" to={deepHref} className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest underline underline-offset-2 hover:opacity-80">
            Review Remittance <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}
